import { FC, useContext, useEffect, useRef, useState } from "react";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../context/notificationContext";
import { useUserSettings } from "../../context/userSettingsContext";
import { useI18n } from "../../hooks/useTranslations";
import { useAdminCarsApi } from "../../http/cars/api";
import { useRentalCarApi } from "../../http/rentalCars/api";
import { User } from "../../http/users/types";
import ModalDialog from "../ui/ModalDialog";
import RentalCarForm, { RentalCarFormBody } from "./RentalCarForm";

interface CreateRentalCarButtonProps {
  carId?: string;
  onClose: (success?: boolean) => void;
  driver?: User;
}

const CreateRentalCarDialog: FC<CreateRentalCarButtonProps> = ({ carId, onClose, driver }) => {
  const i18n = useI18n();
  const dispatch = useContext(NotificationDispatch);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const userSettings = useUserSettings();
  const carDriversApi = useRentalCarApi();
  const carsApi = useAdminCarsApi();
  const aborter = useRef(new AbortController());

  const { data, trigger } = carsApi.getCar(carId!, aborter.current.signal);

  useEffect(() => {
    carId && trigger();
  }, [carId]);

  const onSubmit = async (formData: RentalCarFormBody) => {
    setIsSubmitting(true);
    try {
      await carDriversApi.insertAdminRentalCar({
        carId: carId ?? formData.car.id,
        driverId: formData.driver.id,
        startDate: formData.startDate,
        endDate: formData.endDate,
        plateNumber: formData.plateNumber,
        leasingCompanyId: formData.leasingCompany.id,
        manufacturerId: formData.manufacturer?.id,
        model: formData.model,
      });
      dispatch(showSuccessNotification(i18n.t));
      onClose(true);
    } catch (error) {
      dispatch(showErrorNotification(i18n, error));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ModalDialog onClose={onClose} title={i18n.t.rentalCars.new}>
      <RentalCarForm
        isDisabled={isSubmitting || !userSettings.permissions.rentalCars.insert}
        onSubmit={onSubmit}
        carId={carId}
        driver={driver || data?.currentAssignment.carDriver?.driver}
      />
    </ModalDialog>
  );
};

export default CreateRentalCarDialog;
